import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { Observable, delay, of } from 'rxjs';
import { environment } from '@pt/environment';
import { RootState } from '@pt/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonModule,
  CheckboxComponentModule,
  ControlType,
  DIALOG_DATA,
  DialogRef,
  EmojiModule,
  IconModule,
  NormalizeNumberDirective,
  SearchSelectModule,
  SimpleInputModule,
  SpinnerModule,
  TextAreaComponentModule,
  ToastType,
} from '@hglobal/common';
import { Store } from '@ngrx/store';
import { getUsersMock } from '@pt/auth';
import { FormType, P2PLimits, UserProfile } from '@pt/models';
import { AvatarModule, CoinComponent, UserProfileCardModule, BalanceActions } from '@pt/components';
import {
  UiStore, GoogleAnalyticEventAction, GoogleAnalyticsService, ToastI18nService,
} from '@pt/services';
import { TextFormaterHelper } from '@pt/helpers';

import { AccrueUserRes, AccrualUserForm } from './models';
import { P2pSharePolicyDialog } from './components/p2p-share-policy/p2p-share-policy.dialog';

export type AccrualType = 'admin' | 'site';

@Component({
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    IconModule,
    SimpleInputModule,
    ReactiveFormsModule,
    SearchSelectModule,
    AvatarModule,
    UserProfileCardModule,
    SpinnerModule,
    EmojiModule,
    TextAreaComponentModule,
    CheckboxComponentModule,
    NormalizeNumberDirective,
    CoinComponent,
  ],
  selector: 'pu-accrual-user-form',
  templateUrl: './accrual-user-form.component.html',
  styleUrls: ['./accrual-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [P2pSharePolicyDialog],
})
export class AccrualUserFormComponent implements OnInit {
  readonly uiStore = inject(UiStore);
  readonly router = inject(Router);

  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);
  readonly #coinIconName = `${environment.shortName}-coin`;

  controlTypes = ControlType;
  form: FormType<AccrualUserForm & { isAgreed?: boolean }>;
  isSaving = false;
  api: string;
  type: AccrualType;
  user: UserProfile;
  limits: P2PLimits | null;
  usersMock = getUsersMock();// TODO: import mocks for search-select component is kind of weird IMHO
  openInitiator: string;

  get maxAmount(): number | null {
    return this.type === 'site' && !this.limits?.approvalEnabled ? this.limits?.perOne : null;
  }

  constructor(
    private _store: Store<RootState>,
    private _dialogRef: DialogRef,
    private _fb: FormBuilder,
    private _translate: TranslateService,
    private _http: HttpClient,
    private _cd: ChangeDetectorRef,
    private _toast: ToastI18nService,
    private _policyDialog: P2pSharePolicyDialog,
    @Inject(DIALOG_DATA) public data: { type: AccrualType; user: UserProfile; limits?: P2PLimits },
  ) {
    this.type = data.type;
    this.user = data.user;
    this.limits = data.limits || null;
  }

  ngOnInit() {
    this.form = this._fb.group({
      user: [
        // @ts-ignore
        { value: <UserProfile>(this.user ? this.user : null), disabled: !!this.user },
        Validators.required,
      ],
      amount: [1, Validators.required],
      comment: '',
    });

    if (this.type === 'site') {
      this.form.addControl('isAgreed', new FormControl(false, Validators.requiredTrue));
    }

    this.openInitiator = this.user ? 'birthday_section' : 'balance_card';
    this._pushGoogleAnalyticEvent('open');
  }

  accrue() {
    this.isSaving = true;

    this.api = this.type === 'admin' ? 'emission/admin/manual/user/' : 'banking/share/';

    const { user, isAgreed, ...value } = this.form.getRawValue();

    const userAccrual = {
      userId: user.id,
      ...value,
    };

    let accrueUser$: Observable<AccrueUserRes>;

    if (environment.useMocks) {
      accrueUser$ = of(null).pipe(delay(500));
    } else {
      accrueUser$ = this._http.post<AccrueUserRes>(
        `${environment.apiHost}${this.api}`,
        userAccrual,
      );
    }

    accrueUser$.subscribe({
      next: () => {
        this._dialogRef.close(true);

        if (this.type === 'site') {
          this._store.dispatch(BalanceActions.getBalanceReq.action());
        }
        this._pushGoogleAnalyticEvent('share_button', { status: 'success' });
        this._pushGoogleAnalyticEvent(
          'success',
          { sum: userAccrual.amount, text: userAccrual.comment },
        );
      },
      error: error => {
        this.isSaving = false;
        this._cd.detectChanges();

        if (error.code === 'transfer_limit') {
          this._toast.show(
            ToastType.Error,
            this._translate.instant(
              'site.accrualUserForm.toast',
              { remainingLimit: error.data.remainingLimit },
            ),
            null,
            { iconName: this.#coinIconName },
          );
        }

        if (error.code === 'balance_negative') {
          this._toast.show(
            ToastType.Error,
            'site.accrualUserForm.toast.balance.error-message',
            null,
            { iconName: this.#coinIconName },
          );
        }

        this._pushGoogleAnalyticEvent(
          'share_button',
          { status: TextFormaterHelper.transformToSnakeCase(error.code) },
        );
      },
    });
  }

  openPolicy() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/faq/policies']),
    );

    window.open(url, '_blank');
  }

  close(isCloseByIcon = false): void {
    this._dialogRef.close();
    if (isCloseByIcon) {
      this._pushGoogleAnalyticEvent('close');
    }
  }

  private _pushGoogleAnalyticEvent(
    eventAction: GoogleAnalyticEventAction,
    payload?: { status?: string; sum?: number; text?: string },
  ): void {
    this.#googleAnalyticsService.pushCustomEvent(
      'share_coins',
      eventAction,
      {
        element_name: this.openInitiator,
        id: `${this.user?.id ?? ''}`,
        ...(payload ?? {}),
      },
    );
  }
}
